/**
 * @lines98/client/components/meta-badge — ПРИМИТИВЫ топлайн-бейджей мета-карточек:
 * УРОВЕНЬ (фикс-круг + число) и ПРЕСТИЖ/КАРАТ (гранёный самоцвет + число, БЕЗ КАПА).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (code-structure §2 «примитив → components/»; owner-directive
 * 2026-06-14 «уровень и престиж — БРАТЬ из зверей, это готовые настроенные примитивы; если
 * чего-то нет среди примитивов — СОЗДАЁМ примитив по правилам, потом используем»). Топлайн
 * звериной карточки (`jewelry-card`) рисовал уровень и карат ИНЛАЙНОМ (`jewelry-card-
 * badge--level` / `--karat`) — это не были переиспользуемые файлы-примитивы, а вёрстка
 * внутри экрана-карточки. Карточка гнома должна показывать ТОТ ЖЕ уровень-бейдж и ТОТ ЖЕ
 * престиж-бейдж (owner: «брать из зверей»). Поэтому общий слой извлечён сюда — обе карточки
 * (звери/гномы) монтируют ОДИН примитив, не копируют вёрстку.
 *
 * Два бейджа (owner-эскиз):
 *   · УРОВЕНЬ  (`createMetaLevelBadge`) — спокойный фикс-круг-«жетон» с числом; форма не
 *     меняется, только число. Модификатор `--max` (акцент) на максимуме. (= визуал
 *     `jewelry-card-badge--level`.)
 *   · ПРЕСТИЖ/КАРАТ (`createPrestigeBadge`) — гранёный самоцвет-шестигранник (KARAT_BADGE_
 *     GLYPH) с числом внутри. **БЕЗ ВЕРХНЕГО КАПА на значение** (owner-directive 2026-06-14:
 *     престиж бесконечен, никаких потолков). У зверей — карат-тир 0..4 (цвет/свечение по
 *     тиру); у гнома — счётчик престижей (тот же силуэт, опц. тир). (= визуал
 *     `jewelry-card-badge--karat`.)
 *
 * **Жёсткая совместимость baseline (jewelry не сломать):** примитив эмитит ТЕ ЖЕ классы и
 * структуру, что прежний инлайн звериной карточки (`jewelry-card-badge*`), плюс собственный
 * `meta-badge*`-класс. Поэтому baseline Мастерской — БАЙТ-В-БАЙТ. Размеры/тиры остаются в
 * jewelry-card.css (потребитель), общий каркас бейджа — здесь (meta-badge.css).
 *
 * **НИКАКОГО ВЕРХНЕГО КАПА (owner-directive 2026-06-14/06-12 §0.G.5):** размеры — формулы от
 * cell с нижним floor; значения (уровень/престиж/карат) печатаются как есть, не капаются.
 *
 * a11y (§8): бейдж несёт `aria-label` (смысл числа); глиф/иконка декоративны.
 *
 * Cross-references:
 *   - components/jewelry-card.ts (ветка: уровень+карат) / gnome-card.ts (ветка: уровень+престиж).
 *   - components/meta-icons.ts (KARAT_BADGE_GLYPH — гранёный силуэт престижа/карата).
 *   - components/level-badge.ts (ОТДЕЛЬНЫЙ примитив — бейдж ПРОГРЕССИИ ИГРОКА у ника/в
 *     Профиле; НЕ путать: тот про аккаунт-уровень, этот — про мета-карточку зверя/гнома).
 *   - meta-badge.css (co-located; размеры от --cell-size, без капов).
 */

import './meta-badge.css';

import { t } from '../lib/i18n/index.js';
import { KARAT_BADGE_GLYPH } from './meta-icons.js';

// =============================================================================
// УРОВЕНЬ — фикс-круг-«жетон» + число (= jewelry-card-badge--level).
// =============================================================================

export interface MetaLevelBadgeOptions {
  /** Доп. класс(ы) на корень — surface-обёртка (`jewelry-card-badge--level` и т.п.). */
  readonly className?: string;
  /**
   * Доп. класс(ы) на элемент-число — surface-совместимость (звериный baseline ждёт
   * `jewelry-card-badge-num`; gnome не задаёт → только базовый `meta-badge-num`).
   */
  readonly numClassName?: string;
  /**
   * a11y-метка при максимуме (по умолчанию «Максимальный уровень»). Не на максимуме aria —
   * «Уровень N» формируется автоматически.
   */
  readonly maxLabel?: string;
  /**
   * Класс-модификатор «максимум» (по умолчанию `meta-badge--max`). Звериный baseline ждёт
   * `jewelry-card-badge--max` — потребитель задаёт его, чтобы скриншоты не менялись.
   */
  readonly maxClassName?: string;
}

export interface MetaLevelBadgeHandle {
  readonly element: HTMLElement;
  /** Обновить уровень. `isMax` → акцентный вид + «Максимальный уровень» в aria. */
  set(level: number, isMax: boolean): void;
}

/**
 * Создать бейдж УРОВНЯ мета-карточки (фикс-круг + число). Возвращает handle; потребитель
 * вставляет `handle.element` в топлайн и обновляет `set(level, isMax)`.
 */
export function createMetaLevelBadge(opts: MetaLevelBadgeOptions = {}): MetaLevelBadgeHandle {
  const root = document.createElement('span');
  root.className = 'meta-badge meta-badge--level';
  if (opts.className) root.classList.add(...opts.className.split(/\s+/).filter(Boolean));

  const num = document.createElement('span');
  num.className = 'meta-badge-num';
  if (opts.numClassName) num.classList.add(...opts.numClassName.split(/\s+/).filter(Boolean));
  root.appendChild(num);

  const maxLabel = opts.maxLabel ?? t('game.meta.maxLevel');
  const maxClass = opts.maxClassName ?? 'meta-badge--max';

  return {
    element: root,
    set(level: number, isMax: boolean): void {
      num.textContent = String(level);
      // База + опц. surface-модификатор (звериный baseline ждёт `jewelry-card-badge--max`).
      root.classList.toggle('meta-badge--max', isMax);
      if (maxClass !== 'meta-badge--max') root.classList.toggle(maxClass, isMax);
      root.setAttribute('aria-label', isMax ? maxLabel : t('game.meta.levelAria', { n: level }));
    },
  };
}

// =============================================================================
// ПРЕСТИЖ / КАРАТ — гранёный самоцвет + число (= jewelry-card-badge--karat), БЕЗ КАПА.
// =============================================================================

/** Визуальный тир престижа/карата 0..4 (цвет/свечение по тиру — у зверей). Опц. */
export type PrestigeTier = 0 | 1 | 2 | 3 | 4;

export interface PrestigeBadgeOptions {
  /** Доп. класс(ы) на корень — surface-обёртка (`jewelry-card-badge--karat` и т.п.). */
  readonly className?: string;
  /** Доп. класс(ы) на элемент-число — surface-совместимость (`jewelry-card-badge-num`). */
  readonly numClassName?: string;
  /** Префикс a11y-метки числа (напр. «Карат» / «Престижей»). По умолчанию «Престижей». */
  readonly ariaPrefix?: string;
  /**
   * Префикс класса тира (по умолчанию `meta-badge--prestige-t`; ставится `…t0..t4`).
   * Звериный baseline ждёт `jewelry-card-badge--karat-t` — потребитель задаёт его, чтобы
   * скриншоты/тесты тиров не менялись.
   */
  readonly tierClassPrefix?: string;
}

export interface PrestigeBadgeHandle {
  readonly element: HTMLElement;
  /**
   * Поставить значение + тир. `value` — целое (престиж/карат), **БЕЗ ВЕРХНЕГО КАПА**
   * (owner-directive 2026-06-14: печатаем как есть). `tier` 0..4 управляет материалом/
   * свечением (CSS); по умолчанию 0.
   */
  set(value: number, tier?: PrestigeTier): void;
}

/**
 * Создать бейдж ПРЕСТИЖА/КАРАТА (гранёный самоцвет-шестигранник + число внутри). БЕЗ
 * верхнего капа на значение. Возвращает handle; потребитель вставляет `handle.element`.
 */
export function createPrestigeBadge(opts: PrestigeBadgeOptions = {}): PrestigeBadgeHandle {
  const root = document.createElement('span');
  root.className = 'meta-badge meta-badge--prestige';
  if (opts.className) root.classList.add(...opts.className.split(/\s+/).filter(Boolean));
  // Гранёный силуэт (свопабельный плейсхолдер; материал/свечение по тиру — CSS).
  root.innerHTML = KARAT_BADGE_GLYPH;
  const num = document.createElement('span');
  num.className = 'meta-badge-num';
  if (opts.numClassName) num.classList.add(...opts.numClassName.split(/\s+/).filter(Boolean));
  root.appendChild(num);

  const ariaPrefix = opts.ariaPrefix ?? t('game.meta.prestigeCount');
  const tierPrefix = opts.tierClassPrefix ?? 'meta-badge--prestige-t';

  return {
    element: root,
    set(value: number, tier: PrestigeTier = 0): void {
      // СВОЙСТВО ПРИМИТИВА (owner-directive 2026-06-15): нулевой/отрицательный престиж — значка
      // НЕТ (исчезает); появляется только при значении > 0. Потребитель не управляет hidden.
      root.hidden = !(value > 0);
      if (value <= 0) return;
      // Значение НЕ капается (owner: верхний кап запрещён) — печатаем как есть.
      num.textContent = String(value);
      // Число ВПИСЫВАЕТСЯ в центральную плоскую грань самоцвета (≈73% ширины бокса —
      // шестигранник 3.2..20.8 из 24). Длинные числа (≥4 цифр) авто-уменьшаются по кеглю,
      // чтобы НЕ наезжать на грани (owner-directive 2026-06-14); ≤3 цифр — штатный кегль
      // (зверь-карат не меняется). Кегль = доля размера самоцвета: 4 цифры·0.55em·(1.2/n)=0.66
      // ширины < 0.73 грани. (1.2/n: каждая лишняя цифра пропорц. ужимает текст.)
      const digits = String(value).length;
      num.style.fontSize =
        digits >= 4 ? `calc(var(--mb-prestige) * ${(1.0 / digits).toFixed(3)})` : '';
      // Снять базовые + опц. surface-тиры (звериный baseline ждёт `jewelry-card-badge--karat-t`).
      for (let t = 0; t <= 4; t++) {
        root.classList.remove(`meta-badge--prestige-t${t}`);
        if (tierPrefix !== 'meta-badge--prestige-t') root.classList.remove(`${tierPrefix}${t}`);
      }
      root.classList.add(`meta-badge--prestige-t${tier}`);
      if (tierPrefix !== 'meta-badge--prestige-t') root.classList.add(`${tierPrefix}${tier}`);
      root.setAttribute('aria-label', `${ariaPrefix}: ${value}`);
    },
  };
}
